/**
 * Phase 135E — StreamX Featured This Week Prestige Badge
 * -----------------------------------------------------
 * Public-facing only. Renders on /livex/academy/{slug}/top-moments when the
 * academy is the Story of the Week, Rising Academy, or Most Shared Moment
 * holder in the current 7-day window.
 *
 * Doctrine: verified prestige — NOT gamified sticker. Premium glassmorphism
 * with gold/orange shimmer. Designed for screenshots: every pixel is meant
 * to be shared on WhatsApp / Instagram / sponsor decks.
 *
 * Hover/click → links to /livex/stories so editorial circulation compounds.
 */
import { Link } from 'react-router-dom';
import { Sparkles, ExternalLink } from 'lucide-react';

const SECONDARY_LABELS = {
  story_of_the_week: 'Story of the Week',
  rising_academy: 'Rising Academy',
  most_shared_moment: 'Most Shared Moment',
};

export default function LivexFeaturedBadge({ featured }) {
  if (!featured || !featured.type) return null;
  const { label, emoji, headline, subline, accent, also_featured_as = [] } = featured;
  const extraCount = Array.isArray(also_featured_as) ? also_featured_as.length : 0;
  const extraLabels = (also_featured_as || []).map((t) => SECONDARY_LABELS[t] || t).filter(Boolean);

  return (
    <div
      data-testid="livex-featured-badge"
      data-feature-type={featured.type}
      className="relative inline-flex items-stretch group"
    >
      {/* Glow halo */}
      <div
        aria-hidden
        className="absolute -inset-1 rounded-2xl blur-xl opacity-50 group-hover:opacity-80 transition-opacity"
        style={{
          background: `linear-gradient(135deg, ${accent}66, #fbbf2455 60%, transparent)`,
        }}
      />

      {/* Pill body — glassmorphism + double border */}
      <Link
        to="/streamx/stories"
        data-testid="livex-featured-badge-link"
        title="Featured on StreamX Stories this week"
        className="relative inline-flex items-center gap-3 px-4 py-2.5 rounded-2xl backdrop-blur-md transition-all hover:-translate-y-0.5"
        style={{
          background: 'linear-gradient(135deg, rgba(251,191,36,0.16), rgba(15,23,42,0.78) 65%)',
          border: `2px solid ${accent}`,
          boxShadow: `0 12px 32px ${accent}33, inset 0 0 0 1px rgba(255,255,255,0.12)`,
        }}
      >
        {/* Animated shimmer overlay (CSS keyframes inline) */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
        >
          <span
            className="absolute inset-y-0 -left-1/2 w-1/2 livex-featured-shimmer"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)',
            }}
          />
        </span>

        {/* Trophy emoji */}
        <span
          className="relative flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0 text-xl"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}aa)`,
            boxShadow: `0 6px 16px ${accent}55`,
          }}
          aria-hidden
        >
          {emoji}
        </span>

        {/* Text */}
        <div className="relative flex flex-col items-start text-left">
          <span
            className="text-[10px] font-black uppercase tracking-[0.18em]"
            style={{ color: accent }}
          >
            <Sparkles className="inline h-3 w-3 -mt-0.5 mr-1" />
            Featured this week
          </span>
          <span className="text-sm font-black text-white tracking-tight leading-tight">
            {label}
          </span>
          {extraCount > 0 && (
            <span
              className="mt-0.5 text-[10px] font-black uppercase tracking-[0.1em] text-white/70"
              data-testid="livex-featured-badge-extras"
              title={extraLabels.join(' · ')}
            >
              + {extraCount} more recognition{extraCount > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <ExternalLink className="relative h-3.5 w-3.5 ml-1" style={{ color: accent }} />
      </Link>

      <style>{`
        @keyframes livex-featured-shimmer {
          0% { transform: translateX(0%); }
          60% { transform: translateX(420%); }
          100% { transform: translateX(420%); }
        }
        .livex-featured-shimmer {
          animation: livex-featured-shimmer 3.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

/**
 * Footer strip — "Recognized by StreamX" — placed at the bottom of the brand page
 * for additional perceived legitimacy. Quieter than the headline badge.
 */
export function LivexFeaturedFooterStrip({ featured }) {
  if (!featured || !featured.type) return null;
  return (
    <div
      data-testid="livex-featured-footer-strip"
      className="mx-auto my-6 max-w-3xl px-5 py-4 rounded-2xl border-2 flex items-center gap-3 backdrop-blur"
      style={{
        background: 'linear-gradient(135deg, rgba(251,191,36,0.08), rgba(15,23,42,0.65))',
        borderColor: `${featured.accent}55`,
      }}
    >
      <span
        className="h-10 w-10 rounded-xl grid place-items-center text-lg flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${featured.accent}, ${featured.accent}aa)` }}
        aria-hidden
      >
        {featured.emoji}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-black uppercase tracking-[0.15em]" style={{ color: featured.accent }}>
          Recognized by StreamX
        </div>
        <div className="text-sm font-bold text-white">{featured.subline}</div>
      </div>
      <Link
        to="/streamx/stories"
        data-testid="livex-featured-footer-link"
        className="text-xs font-black uppercase tracking-[0.1em] text-white hover:opacity-80 inline-flex items-center gap-1 flex-shrink-0"
      >
        See stories <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
}
